import { fetchClient } from "@app/config/api/api";
import { sortSummariesNewestFirst } from "./researchSummarySort";
export { sortSummariesNewestFirst } from "./researchSummarySort";

export interface CreateResearchSessionInput {
  experiment_id: string;
  question: string;
  urls?: string[];
  attachment_ids?: string[];
  avoid_constraints?: string;
  tools_selected?: string[];
  models_selected?: Record<string, unknown>;
  budgets?: Record<string, unknown>;
}

export interface CreatedResearchSession {
  id: string;
  status: "pending" | "running" | "succeeded" | "cancelled" | "failed";
  question: string;
  created_at: string;
}

export async function createResearchSession(input: CreateResearchSessionInput): Promise<CreatedResearchSession> {
  const r = await (
    fetchClient.POST as unknown as (
      path: string,
      init: { body: CreateResearchSessionInput }
    ) => Promise<{ data?: CreatedResearchSession; error?: unknown; response: Response }>
  )("/research-tasks/research_sessions/", { body: input });

  if (r.error) {
    throw new Error(`createResearchSession failed: ${JSON.stringify(r.error)}`);
  }
  if (!r.data) {
    throw new Error(`createResearchSession returned empty body (HTTP ${r.response.status})`);
  }
  return r.data;
}

export async function getSummaryBySessionId(
  experimentId: string,
  sessionId: string
): Promise<{ id: string; session_id: string } | null> {
  const r = await (
    fetchClient.GET as unknown as (path: string) => Promise<{
      data?: Array<{ id: string; session_id: string }>;
      error?: unknown;
      response: Response;
    }>
  )(`/research-tasks/research_summaries/?experiment_id=${experimentId}`);

  if (r.error) {
    throw new Error(`getSummaryBySessionId failed: ${JSON.stringify(r.error)}`);
  }
  const summaries = r.data ?? [];
  return summaries.find(s => s.session_id === sessionId) ?? null;
}

export interface ResearchSummaryDetail {
  id: string;
  session_id: string;
  experiment_id: string;
  title: string;
  markdown_body: string;
  markdown_body_original: string;
  sources: Array<{ cite_id: string; title: string; authors: string; year: number; doi?: string }>;
  sources_original: Array<unknown>;
  edit_count: number;
  last_edited_at: string | null;
  last_edited_by_id: string | null;
  created_at: string;
}

export async function getResearchSummary(summaryId: string): Promise<ResearchSummaryDetail> {
  const r = await (
    fetchClient.GET as unknown as (
      path: string
    ) => Promise<{ data?: ResearchSummaryDetail; error?: unknown; response: Response }>
  )(`/research-tasks/research_summaries/${summaryId}/`);

  if (r.error) {
    throw new Error(`GET summary failed: ${JSON.stringify(r.error)}`);
  }
  if (!r.data) {
    throw new Error(`GET summary returned empty body (HTTP ${r.response.status})`);
  }
  return r.data;
}

export async function updateResearchSummary(
  summaryId: string,
  body: { markdown_body: string; edit_instruction?: string }
): Promise<ResearchSummaryDetail> {
  const r = await (
    fetchClient.PUT as unknown as (
      path: string,
      init: { body: typeof body }
    ) => Promise<{ data?: ResearchSummaryDetail; error?: unknown; response: Response }>
  )(`/research-tasks/research_summaries/${summaryId}/edit/`, { body });

  if (r.error) {
    throw new Error(`PUT summary edit failed: ${JSON.stringify(r.error)}`);
  }
  if (!r.data) {
    throw new Error(`PUT summary edit returned empty body (HTTP ${r.response.status})`);
  }
  return r.data;
}

export async function resetResearchSummary(summaryId: string): Promise<ResearchSummaryDetail> {
  const r = await (
    fetchClient.POST as unknown as (
      path: string,
      init?: unknown
    ) => Promise<{ data?: ResearchSummaryDetail; error?: unknown; response: Response }>
  )(`/research-tasks/research_summaries/${summaryId}/reset/`, { body: {} });

  if (r.error) {
    throw new Error(`POST summary reset failed: ${JSON.stringify(r.error)}`);
  }
  if (!r.data) {
    throw new Error(`POST summary reset returned empty body (HTTP ${r.response.status})`);
  }
  return r.data;
}

export async function listResearchSummaries(experimentId: string): Promise<ResearchSummaryDetail[]> {
  const r = await (
    fetchClient.GET as unknown as (path: string) => Promise<{
      data?: ResearchSummaryDetail[];
      error?: unknown;
      response: Response;
    }>
  )(`/research-tasks/research_summaries/?experiment_id=${experimentId}`);

  if (r.error) {
    throw new Error(`listResearchSummaries failed: ${JSON.stringify(r.error)}`);
  }
  return sortSummariesNewestFirst(r.data ?? []);
}
